import { toast } from '@/components/ui/use-toast'
import { CircleBackslashIcon, PlusIcon } from '@radix-ui/react-icons'
import { delUrlFetcher } from 'api/swr'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { color2Linear } from 'CanvasCore/utils'
import { makeShortId } from 'commons'
import { ColorCard } from 'components/ui/ColorCard'
import { useUI } from 'Editor'
import {
  useMemo,
  useState,
  type ComponentType,
  type FC,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { useParams } from 'react-router-dom'
import { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { ColorEditorPanel } from './ColorEditorPanel'
import { ColorSwatch } from './ColorSwatch'
import { DEFAULT_GRADIENT } from './constants'
import type { ColorEditorState, Toning } from './types'

const TypedColorCard = ColorCard as ComponentType<
  HTMLAttributes<HTMLDivElement> & { children?: ReactNode }
>
interface ColorGridProps {
  tonings?: Toning[]
}

export const ColorGrid: FC<ColorGridProps> = ({ tonings }) => {
  const { tid } = useParams<{ tid: string }>()
  const toningId = useUI((state) => state.toningId)
  const toning = useMemo(
    () => tonings?.find((t) => t.id === toningId),
    [tonings, toningId],
  )

  const [editorState, setEditorState] = useState<ColorEditorState | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { trigger: deleteColor } = useSWRMutation(
    `/tonings/${toningId}/colors`,
    delUrlFetcher,
    {
      onSuccess: () => {
        mutate(['/tonings', { tid }])
        toast({
          title: 'Toning color Deleted',
          description: 'The Toning color has been successfully deleted.',
          className: 'bg-green-500 text-white',
        })
        setDeletingId(null)
      },
    },
  )

  const selectColor = useEditorStore((state) => state.selectColor)

  const openCreate = () => {
    const colorId = makeShortId(10)
    selectColor(tid!, toningId!, 'default', { id: colorId, color: DEFAULT_GRADIENT })
    setEditorState({
      mode: 'create',
      colorId,
      gradient: DEFAULT_GRADIENT,
    })
  }

  const previewColor = (
    colorId: string,
    colorSet: NonNullable<Toning['colors']>[number],
  ) => {
    const linear = color2Linear(colorSet.colors) ?? DEFAULT_GRADIENT
    selectColor(tid!, toningId!, 'default', { id: colorId, color: linear })
    return linear
  }

  const openEdit = (colorSet: NonNullable<Toning['colors']>[number]) => {
    const linear = previewColor(colorSet.id, colorSet)
    setEditorState({
      mode: 'edit',
      colorId: colorSet.id,
      gradient: linear,
    })
  }

  const openDuplicate = (colorSet: NonNullable<Toning['colors']>[number]) => {
    const newId = makeShortId(10)
    const linear = previewColor(newId, colorSet)
    setEditorState({
      mode: 'duplicate',
      colorId: newId,
      gradient: linear,
    })
  }

  const handleDelete = (colorId: string) => {
    setDeletingId(colorId)
    deleteColor({ tid, colorId })
  }

  if (editorState) {
    return (
      <ColorEditorPanel
        mode={editorState.mode}
        colorId={editorState.colorId}
        initialGradient={editorState.gradient}
        onClose={() => setEditorState(null)}
      />
    )
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 xl:grid-cols-9 grid-cols-9 gap-2 p-1">
        <CleanColorButton />
        <CreateColorButton onClick={openCreate} />
        {toning?.colors?.map((c) => (
          <ColorSwatch
            key={c.id}
            color={c}
            onEdit={() => openEdit(c)}
            onDuplicate={() => openDuplicate(c)}
            onDelete={() => handleDelete(c.id)}
            isDeleting={deletingId === c.id}
          />
        ))}
      </div>
    </div>
  )
}

const CleanColorButton: FC = () => {
  const { tid } = useParams<{ tid: string }>()
  const toningId = useUI((state) => state.toningId)
  const deselectColor = useEditorStore((state) => state.deselectColor)

  return (
    <TypedColorCard onClick={() => deselectColor(tid!, toningId!)}>
      <CircleBackslashIcon className="w-3/5 h-3/5 text-gray-400 group-hover:text-primary" />
    </TypedColorCard>
  )
}

interface CreateColorButtonProps {
  onClick: () => void
}

const CreateColorButton: FC<CreateColorButtonProps> = ({ onClick }) => (
  <TypedColorCard onClick={onClick}>
    <PlusIcon className="w-3/5 h-3/5 text-gray-400 group-hover:text-primary" />
  </TypedColorCard>
)
